/**
 * Executive Overview — replaces the previous generic /dashboard as the
 * platform's first surface. Answers CEO/CRO-level questions:
 *   • How many consumers?
 *   • How many institutions?
 *   • How many workflows running?
 *   • Evidence generated
 *   • Trust certificates
 *   • High-risk alerts
 *   • Platform health
 *
 * Note: /dashboard still routes to the existing Dashboard.jsx for
 * backwards compatibility. This is the new default homepage under
 * /executive-overview and is linked from the sidebar's Overview slot.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import {
  Users, Building2, Workflow, ShieldCheck, FileCheck2, Bell, Activity, ArrowRight, Sparkles,
} from "lucide-react";

const NAVY = "#0A1F44";
const GOLD = "#C9A24A";
const TEAL = "#0D9488";

export default function ExecutiveOverview() {
  const [kpis, setKpis] = useState({
    consumers: null, institutions: null, workflows: null, evidence: null,
    certificates: null, alerts: null, uptime: null,
  });

  useEffect(() => {
    (async () => {
      const next = { consumers: null, institutions: null, workflows: null, evidence: null, certificates: null, alerts: null, uptime: null };
      try { const { data } = await api.get("/consumers"); next.consumers = Array.isArray(data) ? data.length : null; } catch { /* endpoint optional */ }
      try { const { data } = await api.get("/admin/tenants"); next.institutions = Array.isArray(data) ? data.length : null; } catch { /* endpoint optional */ }
      try { const { data } = await api.get("/evidence"); next.evidence = Array.isArray(data) ? data.length : (data?.total ?? null); } catch { /* endpoint optional */ }
      try { const { data } = await api.get("/trust-requests/kpis"); next.workflows = data?.open ?? null; } catch { /* endpoint optional */ }
      // Workflows / certificates / alerts / uptime — honest placeholders until endpoints ship
      setKpis(next);
    })();
  }, []);

  return (
    <div className="max-w-6xl mx-auto p-6 lg:p-10 space-y-8" data-testid="executive-overview">
      <header>
        <div className="text-[10px] uppercase tracking-[0.22em] font-semibold" style={{ color: GOLD }}>
          Executive Overview
        </div>
        <h1 className="font-display text-3xl lg:text-4xl font-semibold" style={{ color: NAVY }}>
          Altara at a glance
        </h1>
        <p className="text-[15px] text-slate-600 max-w-3xl mt-2 leading-relaxed">
          Every consumer, institution, workflow and piece of evidence Altara is currently orchestrating. If the answer isn&apos;t on this page, it isn&apos;t happening on the platform.
        </p>
      </header>

      {/* Top row: 4 primary counters */}
      <section className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="exec-kpis-primary">
        <BigTile icon={Users}       label="Consumers"    value={kpis.consumers}    to="/capability/consumer-intelligence" accent={NAVY} />
        <BigTile icon={Building2}   label="Institutions" value={kpis.institutions} to="/capability/platform"              accent={GOLD} />
        <BigTile icon={Workflow}    label="Workflows"    value={kpis.workflows}    to="/capability/trust-workflows"       accent={TEAL} emptyHint="Send a Trust Request to see this rise" />
        <BigTile icon={ShieldCheck} label="Evidence"     value={kpis.evidence}     to="/capability/governance"            accent={"#065F46"} />
      </section>

      {/* Second row: 3 secondary counters */}
      <section className="grid grid-cols-1 lg:grid-cols-3 gap-3" data-testid="exec-kpis-secondary">
        <BigTile icon={FileCheck2} label="Trust certificates" value={kpis.certificates} to="/trust-certificates" accent={NAVY} emptyHint="Wire on /trust/count" />
        <BigTile icon={Bell}       label="High-risk alerts"   value={kpis.alerts}       to="/capability/governance" accent={"#7F1D1D"} emptyHint="Alerts ship Phase 1.5" />
        <BigTile icon={Activity}   label="Platform health"    value={kpis.uptime}       to="/platform/health"      accent={TEAL} emptyHint="Health probe pending" />
      </section>

      {/* Capability shortcuts ─────────────────────────── */}
      <section>
        <div className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-3">
          Six platform capabilities
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          <CapCard n="1" name="Consumer Intelligence"  to="/capability/consumer-intelligence"  purpose="Unified consumer view — read-only." accent={NAVY} />
          <CapCard n="2" name="Institution Workspace"  to="/capability/institution-workspace"  purpose="What work is on my desk today?" accent={NAVY} />
          <CapCard n="3" name="Trust Workflows"        to="/capability/trust-workflows"        purpose="The orchestration engine." accent={GOLD} />
          <CapCard n="4" name="Consumer Education"     to="/capability/consumer-education"     purpose="Understanding as evidence." accent={NAVY} />
          <CapCard n="5" name="Governance"             to="/capability/governance"             purpose="Prove it — to any regulator." accent={NAVY} />
          <CapCard n="6" name="Platform"               to="/capability/platform"               purpose="The substrate everything runs on." accent={NAVY} />
        </div>
      </section>

      {/* Legacy dashboard link */}
      <section className="rounded-2xl border border-slate-100 bg-white p-4 flex items-start gap-3">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: "#F1F5F9" }}>
          <Sparkles className="w-4 h-4" style={{ color: NAVY }} />
        </div>
        <div className="flex-1">
          <div className="text-[13px] font-semibold" style={{ color: NAVY }}>Looking for the old dashboard?</div>
          <div className="text-[12px] text-slate-500 mt-0.5">The pre-refactor tenant-scoped dashboard is preserved at <code>/dashboard</code>. Charts, activity feed and category breakdown all still live there.</div>
        </div>
        <Link to="/dashboard" data-testid="link-old-dashboard" className="text-[12px] font-semibold self-center px-3 py-1.5 rounded-full border border-slate-200 hover:bg-slate-50" style={{ color: NAVY }}>
          Open →
        </Link>
      </section>
    </div>
  );
}

function BigTile({ icon: Icon, label, value, to, accent, emptyHint }) {
  const empty = value === null || value === undefined;
  const inner = (
    <div
      data-testid={`exec-tile-${label.toLowerCase().replace(/\s+/g, "-")}`}
      className="rounded-2xl bg-white border border-slate-100 shadow-sm p-4 hover:shadow-md hover:border-slate-300 transition h-full"
    >
      <div className="flex items-center gap-2 mb-2">
        <span className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: `${accent}12`, color: accent }}>
          <Icon className="w-3.5 h-3.5" />
        </span>
        <span className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">
          {label}
        </span>
      </div>
      <div className="font-display text-3xl font-semibold" style={{ color: empty ? "#94A3B8" : accent }}>
        {empty ? "—" : value}
      </div>
      <div className="text-[11px] text-slate-500 mt-1">
        {empty ? (emptyHint || "No data yet") : "Live"}
      </div>
    </div>
  );
  return to ? <Link to={to}>{inner}</Link> : inner;
}

function CapCard({ n, name, purpose, to, accent }) {
  return (
    <Link
      to={to}
      data-testid={`exec-cap-${n}`}
      className="group rounded-2xl bg-white border border-slate-100 p-4 hover:border-slate-300 hover:shadow-sm transition"
    >
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center font-display font-semibold" style={{ background: `${accent}12`, color: accent }}>
          {n}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-[15px]" style={{ color: NAVY }}>{name}</div>
          <div className="text-[12px] text-slate-500 mt-0.5">{purpose}</div>
        </div>
        <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-slate-500 self-center" />
      </div>
    </Link>
  );
}
